import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
import { fetchAppointedPoa } from "../../service/settings";

const NO_SIGNATURE = "";

/**
 * Power-of-attorney signature picker for document generation.
 *
 * Pre-selects the matter's Appointed POA from Salesforce (whoever signed
 * the 21-22a). Blank means "not recorded" — nothing is pre-selected and
 * the document generates with the signature line empty. Anyone can pick
 * a name, but only an attorney's generation stamps the signature; for
 * everyone else the document is created unsigned and an attorney applies
 * the signature afterwards.
 */
export default function PoaSignatureSelect({
  matterId,
  value,
  onChange,
  disabled,
  labelId,
  variant,
}) {
  const [choices, setChoices] = useState([]);
  const [canApply, setCanApply] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!matterId) return undefined;
    setLoading(true);
    fetchAppointedPoa(matterId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        setChoices(data.choices || []);
        setCanApply(Boolean(data.can_apply_signature));
        // Pre-select only when the caller holds no choice yet — never
        // clobber a name the user already picked (e.g. after Back/reopen).
        // In apply mode, never pre-select a name that can't be stamped.
        const appointedChoice = (data.choices || []).find(
          (choice) => choice.attorney_name === data.appointed_poa
        );
        const selectable =
          variant !== "apply" || Boolean(appointedChoice?.has_signature_image);
        if (!value && data.appointed_poa && data.is_canonical && selectable) {
          onChange(data.appointed_poa);
        }
      })
      .catch(() => {
        if (!cancelled) setChoices([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId]);

  const options = [
    { value: NO_SIGNATURE, label: "No signature (leave blank)" },
    ...choices.map((choice) => ({
      value: choice.attorney_name,
      // In generation, a name without an uploaded image stays selectable:
      // non-attorneys record the POA name and generate unsigned. Apply
      // exists only to stamp, so there a missing image disables the name.
      label: choice.has_signature_image
        ? choice.attorney_name
        : `${choice.attorney_name} (no signature image uploaded)`,
      disabled: variant === "apply" && !choice.has_signature_image,
    })),
  ];

  const hint = (() => {
    if (variant === "apply") {
      if (!value) {
        return "Pick the attorney whose signature should be stamped on this document.";
      }
      if (!canApply) {
        return "Only an attorney can apply the signature.";
      }
      return `The ${value} signature will be stamped onto this document.`;
    }
    if (!value) {
      return "Power of attorney: whoever signed this case's 21-22a. Leave blank if not recorded.";
    }
    if (!canApply) {
      return "Only an attorney can stamp the signature — the document will be generated unsigned, ready for attorney sign-off.";
    }
    return `The ${value} signature will be stamped on generation.`;
  })();

  return (
    <div data-testid="poa-signature-select">
      <Select
        id={labelId}
        loading={loading}
        value={value || NO_SIGNATURE}
        options={options}
        onChange={onChange}
        disabled={disabled}
        style={{ width: "100%" }}
        getPopupContainer={(trigger) => trigger.parentElement}
      />
      <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 12, lineHeight: 1.4 }}>
        {hint}
      </p>
    </div>
  );
}

PoaSignatureSelect.propTypes = {
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  labelId: PropTypes.string,
  variant: PropTypes.oneOf(["generation", "apply"]),
};

PoaSignatureSelect.defaultProps = {
  matterId: null,
  value: "",
  disabled: false,
  labelId: undefined,
  variant: "generation",
};
